import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import NearbyCitiesLinks from "@/components/NearbyCitiesLinks";
import { useSeo, JsonLd } from "@/lib/seo";
import { Check, ArrowRight, Phone } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const PHOTOBOOTH_FAQS = [
  { q: "Combien de photos puis-je prendre avec le photobooth ?", a: "Les photos numériques sont illimitées sur tous les forfaits. Vos invités peuvent se prendre en photo autant qu'ils le souhaitent pendant toute la durée de la prestation." },
  { q: "Comment récupérer les photos après l'événement ?", a: "Toutes les photos sont accessibles en ligne avec des identifiants sécurisés. Vous pouvez les consulter, les télécharger et les partager facilement avec vos invités." },
  { q: "Le photobooth est-il adapté pour un mariage, un anniversaire ou une entreprise ?", a: "Oui — les forfaits photobooth My Animation conviennent au mariage, à l'anniversaire, à la soirée d'entreprise, à la mairie, à l'association, au baptême, à la communion et à la soirée privée." },
  { q: "Peut-on personnaliser le thème graphique du photobooth ?", a: "Oui, les thèmes sont personnalisables à partir du forfait Essentiel (229 €). Nous adaptons le visuel à vos couleurs, à vos prénoms ou à votre logo." },
  { q: "L'option Poteaux de guidage or + cordon rouge est-elle disponible ?", a: "Oui, cette option est proposée à 45 € en complément de n'importe quel forfait photobooth pour matérialiser élégamment l'espace dédié à vos invités." },
  { q: "Intervenez-vous pour le photobooth en dehors du Nord ?", a: "Nous intervenons dans tout le Nord, le Pas-de-Calais, les Hauts-de-France et la Belgique — de Bousbecque à Lille, Tourcoing, Roubaix, Armentières, Dunkerque, Lens et Arras." },
];

export default function Photobooth() {
  useSeo({
    title: "Location photobooth Nord & Hauts-de-France | My Animation",
    description:
      "Location photobooth pour mariage, anniversaire, entreprise, mairie et événement privé. Photos numériques illimitées, impressions selon forfait et galerie en ligne. Forfaits dès 179 €.",
    path: "/photobooth",
    keywords: "location photobooth Nord, photobooth mariage Hauts-de-France, photobooth entreprise Lille, photobooth Tourcoing, photobooth Roubaix",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Location photobooth",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description:
      "Location photobooth pour mariage, anniversaire, entreprise, mairie, association, baptême, communion et soirée privée. Photos numériques illimitées, impressions selon forfait et galerie en ligne avec identifiants.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Photobooth"
        title={<>Photobooth, <span className="italic gold-text">forfaits clairs.</span></>}
        subtitle="Location photobooth pour mariage, anniversaire, entreprise, mairie, association et événement privé dans les Hauts-de-France, le Nord, le Pas-de-Calais et la Belgique. Photos numériques illimitées sur tous les forfaits, accès en ligne sécurisé."
        img="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=1600&q=80"
      />

      {/* Mentions importantes */}
      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-luxe p-7 md:p-8 reveal">
            <p className="eyebrow mb-3">Inclus dans tous les forfaits</p>
            <h2 className="font-serif-luxe text-2xl md:text-3xl">
              Photos numériques <span className="italic gold-text">illimitées.</span>
            </h2>
            <p className="text-white/60 mt-3 text-sm leading-relaxed">
              Tous les forfaits incluent des photos numériques illimitées, sans limite
              de prises de vue pendant toute la durée de la prestation.
            </p>
          </div>
          <div className="card-luxe p-7 md:p-8 reveal">
            <p className="eyebrow mb-3">Galerie en ligne</p>
            <h2 className="font-serif-luxe text-2xl md:text-3xl">
              Accès <span className="italic gold-text">avec identifiants.</span>
            </h2>
            <p className="text-white/60 mt-3 text-sm leading-relaxed">
              Les photos sont accessibles en ligne avec identifiants sécurisés.
              Partage simple avec vos invités après l'événement.
            </p>
          </div>
        </div>
      </section>

      <CatalogPackages
        category="photobooth"
        title="Nos forfaits officiels"
        subtitle={<>Six formules, <span className="italic gold-text">une qualité unique.</span></>}
        testidPrefix="pack-"
        featuredBadgeLabel="Le plus choisi"
        ctaLabel="Réserver mon photobooth"
        footerNote="Tarifs TTC · TVA non applicable art. 293 B du CGI · Accessoires fournis selon disponibilité, saison et thème de l'événement — composition variable selon le stock du moment"
        showComparator={true}
        comparisonExtraCols={[
          { header: "Impressions", cell: (p) => <span className="text-white/80 text-sm">{p.features.find((f) => /impression/i.test(f)) || "—"}</span> },
        ]}
      />

      {/* SEO section */}
      <section className="py-20 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <div className="card-luxe p-8 md:p-12 reveal">
          <p className="eyebrow">Location photobooth — Hauts-de-France</p>
          <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">
            Idéal mariage, anniversaire, entreprise, mairie,<br/>
            <span className="italic gold-text">association, baptême et communion.</span>
          </h2>
          <p className="text-white/65 mt-4 leading-relaxed">
            My Animation propose la location de photobooth pour mariage, anniversaire,
            entreprise, mairie, association, baptême, communion et soirée privée dans
            les Hauts-de-France, le Nord, le Pas-de-Calais et la Belgique — à Bousbecque,
            Lille, Tourcoing, Roubaix, Armentières, Wervicq-Sud, Capinghem, Comines,
            Halluin, Roncq, Bondues, Mouvaux, Marcq-en-Barœul, La Madeleine, Lambersart,
            Villeneuve-d'Ascq, Dunkerque, Hazebrouck, Béthune, Lens, Arras, Valenciennes,
            Douai et Calais. Photos numériques illimitées sur tous les forfaits,
            impressions selon le forfait, thèmes personnalisables selon l'événement et
            accès en ligne aux photos avec identifiants.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact" data-testid="photobooth-cta-final" className="btn-gold">
              Réserver mon photobooth <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <a href="tel:0769224956" className="btn-ghost-gold">
              <Phone size={14} strokeWidth={1.4} /> 07 69 22 49 56
            </a>
          </div>
        </div>
      </section>

      {/* Mini FAQ photobooth */}
      <section className="py-20 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1000px] mx-auto" data-testid="photobooth-faq-section">
        <div className="text-center mb-12 reveal">
          <span className="eyebrow">FAQ Photobooth</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-[1.05]">
            Vos questions sur <span className="italic gold-text">nos forfaits.</span>
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="photobooth-faq-accordion">
          {PHOTOBOOTH_FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`pb-faq-${i}`}
              data-testid={`photobooth-faq-item-${i}`}
              className="card-luxe !rounded-none px-6 md:px-8"
            >
              <AccordionTrigger className="font-serif-luxe text-lg md:text-xl text-left hover:no-underline data-[state=open]:text-[#DCB457] py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/65 text-base leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <NearbyCitiesLinks
        eyebrow="Location photobooth près de chez vous"
        title={<>Photobooth <span className="italic gold-text">dans votre ville.</span></>}
        urlPrefix="photobooth"
        intro="Basée à Bousbecque, My Animation propose la location de photobooth partout dans le Nord, le Pas-de-Calais, les Hauts-de-France et en Belgique frontalière. Voici les pages dédiées ville par ville."
        citySlugs={["lille", "tourcoing", "roubaix", "armentieres", "dunkerque", "lens", "arras", "valenciennes", "cambrai", "bethune"]}
        testidPrefix="photobooth-nearby"
      />

      <CTAFooter />
    </>
  );
}

